/**
 * Wie lange eine Anfrage warten darf.
 *
 * Ohne Grenze belegt eine Anfrage, die nie beantwortet wird, für immer eine Verbindung. Ein Browser
 * hält je Host nur etwa sechs offen — sind sie vergeben, wartet alles Weitere, ohne dass irgendwo
 * ein Fehler erschiene: Die Seite steht auf Ladeskeletten, der Hauptthread rechnet nichts, die
 * Konsole bleibt still. Genau so sieht ein „hängendes" Portal aus.
 *
 * Eine Grenze macht daraus einen Fehler, und ein Fehler ist behandelbar.
 */

/**
 * Fünfundvierzig Sekunden.
 *
 * Großzügig für eine Abfrage, die Daten sammelt, und kurz genug, dass eine verlorene Anfrage die
 * Verbindung nicht über die ganze Sitzung hinweg besetzt.
 */
export const REQUEST_TIMEOUT_MS = 45_000;

/**
 * Ein Signal, das nie auslöst — für Anfragen, die dauern dürfen.
 *
 * Uploads und Datenströme sind nicht langsam, sondern lang: Ein Grundriss geht über die Leitung,
 * ein DWG-Import meldet minutenlang seinen Fortschritt. Sie tragen dieses Signal und werden von
 * der Grenze nicht erfasst.
 */
export const PATIENT_REQUEST: AbortSignal = new AbortController().signal;

/**
 * Welches Abbruchsignal eine Anfrage bekommt — oder `undefined`, wenn sie keines braucht.
 *
 * Die Regel in einem Satz: **Wer selbst eines mitbringt, behält seines; wer etwas hochlädt, bekommt
 * keines; alle übrigen bekommen die Grenze.**
 *
 * Das eigene Signal hat Vorrang, weil der Aufrufer mehr weiß — er bricht etwa ab, wenn seine
 * Ansicht verschwindet. `PATIENT_REQUEST` ist der ausdrückliche Verzicht und geht denselben Weg.
 */
export const requestSignal = (
    options: { signal?: AbortSignal | null; body?: unknown },
    timeoutMs: number = REQUEST_TIMEOUT_MS,
): AbortSignal | undefined => {
    if (options.signal) {
        return options.signal;
    }

    // Ein Upload dauert so lange, wie die Datei braucht. Eine Grenze darauf wäre eine Grenze für
    // die Dateigröße — und zwar eine, die niemand genannt hat.
    if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
        return undefined;
    }

    // Fehlt die Umgebungsfunktion (ältere Browser, Testumgebungen), bleibt es beim bisherigen
    // Verhalten: lieber ohne Grenze als mit einem Absturz.
    if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') {
        return undefined;
    }

    return AbortSignal.timeout(timeoutMs);
};
